import { Observable, EMPTY, forkJoin, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import {
  IFeedbackResponse,
  IFeedbackResponseDetails,
} from '../models/application-details.model';
import { IIrembogovBasicLabelKeyPair } from '../models/irembogov-basic-label-keypair.model';
import { EIremboFormlyFormStateKeys } from '../models/irembo-formly-formstate-keys.enum';

export const GLOBAL_RFA_MESSAGE = `Locate all sections tagged '<span style="color: #dc6803;">Action Required</span>' and update them according to the officer's comments.`;

export interface RfaFetchResult {
  model: Record<string, unknown>;
  formState: Record<string, unknown>;
  isJourneyApplication: boolean;
  journeyFeedbacks: IFeedbackResponseDetails[];
  responsesTracker: boolean[];
}

/**
 * Fetches RFA data with Journey V3 support
 * Handles conditional API calls and form state construction based on application type
 *
 * @param applicationsService - Service instance with API methods
 * @param applicationId - Application ID to fetch data for
 * @param appStatus - Application status object
 * @param rfaObservable - Observable for RFA object and form state
 * @param formStateObservable - Observable for form state
 * @returns Observable with RFA fetch result including Journey V3 metadata
 */
export function fetchRfaDataWithJourneySupport(
  applicationsService: any,
  applicationId: string,
  appStatus: any,
  rfaObservable: Observable<Record<string, unknown>>,
  formStateObservable: Observable<Record<string, unknown>>
): Observable<RfaFetchResult> {
  const applicationDetailsObservable = applicationsService
    .getApplicationDetailsById(applicationId)
    .pipe(catchError((error: any) => of({ error })));

  return applicationDetailsObservable.pipe(
    switchMap((details: any) => {
      // If details fetch failed, return empty to trigger error handling in subscriber
      if (details?.error) {
        return EMPTY;
      }

      const applicationType = details?.applicationType;
      const isJourneyApplication = applicationType === 'JOURNEY_V3';

      // Conditionally fetch journey or regular feedbacks based on application type
      const applicationFeedbacksObservable = (
        isJourneyApplication
          ? applicationsService.getApplicationJourneyFeedbacks(applicationId)
          : applicationsService.getApplicationFeedbacks(applicationId)
      ).pipe(catchError((error: any) => of(error)));

      return forkJoin({
        model: rfaObservable,
        formState: formStateObservable,
        feedbacks: applicationFeedbacksObservable,
      }).pipe(
        map(joinedResponses => {
          // Track which responses succeeded
          const responsesTracker: boolean[] = [
            !!joinedResponses.model,
            !!joinedResponses.formState,
            !!joinedResponses.feedbacks,
          ];

          // Extract model and form state
          const model = joinedResponses.model as Record<string, unknown>;
          const retrievedFormState = (joinedResponses.formState ??
            {}) as Record<string, any>;
          retrievedFormState[EIremboFormlyFormStateKeys.APPLICATION_STATE] =
            appStatus;

          // Process feedback response
          const feedbackResponse = (joinedResponses.feedbacks ??
            {}) as IFeedbackResponse;
          const journeyFeedbacks = isJourneyApplication
            ? feedbackResponse?.feedbackResponseDetails ?? []
            : [];

          // Build RFA flagged fields set (only for non-Journey applications)
          const rfaFlaggedFields: Set<string> = new Set();
          if (!isJourneyApplication) {
            feedbackResponse?.feedbackResponseDetails?.forEach(
              (responseDetail: IFeedbackResponseDetails) => {
                if (
                  responseDetail.activeFeedback &&
                  responseDetail?.feedbackFields &&
                  responseDetail?.feedbackFields.length > 0
                ) {
                  responseDetail.feedbackFields.forEach(
                    (feedback: IIrembogovBasicLabelKeyPair<string>): void => {
                      if (feedback?.key) {
                        rfaFlaggedFields.add(feedback.key);
                      }
                    }
                  );
                }
              }
            );
          }

          // Set form state fields
          retrievedFormState[EIremboFormlyFormStateKeys.RFA_FLAGGED_FIELDS] =
            Array.from(rfaFlaggedFields);
          retrievedFormState[EIremboFormlyFormStateKeys.JOURNEY_FEEDBACKS] =
            journeyFeedbacks;
          retrievedFormState[EIremboFormlyFormStateKeys.APPLICANT_TYPE] ??=
            details.applicantType;
          retrievedFormState[
            EIremboFormlyFormStateKeys.APPLICANT_BUSINESS_ID
          ] ??= details.businessId;
          retrievedFormState[
            EIremboFormlyFormStateKeys.APPLICANT_BUSINESS_NAME
          ] ??= details.businessName;
          retrievedFormState[EIremboFormlyFormStateKeys.APPLICANT_USER_ID] ??=
            details.applicantUserId;

          // For Journey V3, allow editing all fields (not just RFA flagged ones)
          if (isJourneyApplication) {
            retrievedFormState[
              EIremboFormlyFormStateKeys.ALLOW_EDITING_ALL_FIELDS
            ] = true;
          }

          return {
            model,
            formState: retrievedFormState,
            isJourneyApplication,
            journeyFeedbacks,
            responsesTracker,
          };
        })
      );
    })
  );
}
